import ActionButton from 'components/ActionButton';
import { Loading } from 'components/loading/Loading';
import React, { useEffect, useState } from 'react';
import {
  fetchOrderDetail,
  markOrderAsDelivered,
  markOrderAsPacked,
  markOrderAsUnpacked,
  refundDeliveryCharges,
  removeOrderFromRoute,
  sendDeliveryEstimation,
  setSalesPersonInOrder,
  updateOperatorComment,
} from 'services/api/orders';
import { CentsToDollar, DATE_FORMATS, MODAL_TYPES, ORDER_BAG_TYPES, ORDER_STATUS, PAYMENT_PROOF_OPTIONS_MAPPING, SALES_CHANNEL_TYPES, USER_ROLES, baseURL, formatAddress, formatDate, getDifferenceBetweenDates, isUserAuthorized, normalizeErrorMessage, safeShowError, showSuccess, timeToString } from 'utils';
import styles from './index.module.scss';
import QRCode from 'qrcode.react';
import OrderItems from './OrderItems';
import BackButton from 'components/BackButton';
import AddOrderToRouteModal from 'components/AddOrderToRouteModal';
import RefundItemsModal from 'components/RefundItemsModal';
import { mapDispatchToProps as authActions } from 'actions/auth';
import { connect } from 'react-redux';
import QRCodeGenerator from 'components/QRCodeGenerator';
import _ from 'lodash';
import AddOrderItemModal from 'components/AddOrderItemModal';
import UpdateOrderItemModal from 'components/UpdateOrderItemModal';
import SavedPaymentMethods from 'components/SavedPaymentMethods';
import Currency from 'components/Currency/Currency';
import CourierDeliveryTypeModal from 'components/CourierDeliveryTypeModal';
import ResendEmailModal from 'components/ResendEmailModal';
import FullRefundModal from 'components/RefundItemsModal/FullRefundModal';
import MakePaymentModal from 'components/PaymentModals/MakePaymentModal';
import { capitalize } from '../../../utils/index';
import OrderTimeline from './OrderTimeline';
import CustomLink from 'components/TableLink';
import InfoModal from 'components/InfoModal';
import AddBagTypesToOrder from 'components/AddBagTypesToOrderModal';
import SalesPersonModal from 'components/SalesPersonModal';
import ImageUploadComponent from 'components/ImageUploadComponent';
import ViewImageModal from 'components/ViewItemImageModal';

const mapStateToProps = (state: any) => ({
  isAuthenticated: !!state.auth.loggedInUser,
  userRoles: state.auth.loggedInUser ? state.auth.loggedInUser.get('roles') : [],
});

const OrderDetails = props => {
  const { orderId } = props.match.params;
  const [order, setOrder] = useState<Record<string, any>>({});
  const [orderItems, setOrderItems] = useState<Record<string, any>>({});
  const [loading, setLoading] = useState<boolean>(true);
  const [fetchError, setFetchError] = useState<string>('');
  const [comment, setComment] = useState<string>('');
  const [updateLoading, setUpdateLoading] = useState<boolean>(false);
  const [deliveryRefundLoading, setDeliveryRefundLoading] = useState<boolean>(false);
  const [openFullRefundModal, setOpenFullRefundModal] = useState<boolean>(false);
  const [estimationLoading, setEstimationLoading] = useState<boolean>(false);
  const [refundButtonDisabled, setRefundButtonDisabled] = useState<boolean>(true);
  const [selectedRowsForRefund, setSelectedRowsForRefund] = useState<Record<string, any>[]>([]);
  const [addToRouteModalOpen, setAddToRouteModalOpen] = useState<boolean>(false);
  const [orderIdForModal, setOrderIdForModal] = useState<string>('');
  const [routeActionLoading, setRouteActionLoading] = useState<boolean>(false);
  const [orderActionLoading, setOrderActionLoading] = useState<boolean>(false);
  const [orderUnPackedActionLoading, setOrderUnPackedActionLoading] = useState<boolean>(false);
  const [refundModalOpen, setRefundModalOpen] = useState<boolean>(false);
  const [addOrderItemModalOpen, setAddOrderItemModalOpen] = useState<boolean>(false);
  const [updateOrderItemModalOpen, setUpdateOrderItemModalOpen] = useState<boolean>(false);
  const [orderItemToUpdate, setOrderItemToUpdate] = useState<Record<string, any>>({});
  const [courierDeliveryTypeModalOpen, setCourierDeliveryTypeModalOpen] = useState<boolean>(false);
  const [resendEmailModalOpen, setResendEmailModalOpen] = useState<boolean>(false);
  const [markPaymentModalOpen, setMarkPaymentModalOpen] = useState<boolean>(false);
  const [paymentMethodModalOpen, setPaymentMethodModalOpen] = useState<boolean>(false);
  const [orderPackedModal, setOrderPackedModal] = useState<boolean>(false);
  const [addBagsToOrderModalOpen, setAddBagsToOrderModalOpen] = useState<boolean>(false);
  const [viewImageModalOpen, setViewImageModalOpen] = useState<boolean>(false);

  const fetchOrderDetailsFn = async () => {
    try {
      setFetchError('');
      const response = await fetchOrderDetail(orderId);
      setOrder(response);
      setOrderItems(response?.items);
      setComment(response.operatorComment);
    } catch (e) {
      // Avoid using toast notifications here because a failing notification render can take down the whole page.
      // Show the actual backend error inline instead.
      setFetchError(normalizeErrorMessage(e));
      // eslint-disable-next-line no-console
      console.error('Failed to fetch order details:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const authorizedRoles = [USER_ROLES.MANAGER, USER_ROLES.PACKER];
    if (!props.isAuthenticated || !isUserAuthorized(props.userRoles, authorizedRoles)) {
      const redirectionURL = `${process.env.REACT_APP_WEB_APP_URL}/orders/ebrpbAjdgx`;
      window.location.assign(redirectionURL);
    } else {
      fetchOrderDetailsFn();
    }
  }, []);

  const sendEstimation = async () => {
    setEstimationLoading(true);
    try {
      await sendDeliveryEstimation(orderId);
      showSuccess('Email for delivery time estimation has been sent successfully');
      fetchOrderDetailsFn();
    } catch (e) {
      safeShowError(e);
    } finally {
      setEstimationLoading(false);
    }
  };

  const updateOperatorCommentFn = async () => {
    setUpdateLoading(true);
    try {
      await updateOperatorComment(orderId, comment);
      showSuccess('Comment updated successfully');
    } catch (e) {
      safeShowError(e);
    } finally {
      setUpdateLoading(false);
    }
  };

  const refundOrderDelivery = async () => {
    setDeliveryRefundLoading(true);
    try {
      await refundDeliveryCharges(order.objectId);
      showSuccess('Delivery refunded successfully');
    } catch (e) {
      safeShowError(e);
    } finally {
      setDeliveryRefundLoading(false);
    }
  };

  const removeFromRoute = async () => {
    setRouteActionLoading(true);
    try {
      await removeOrderFromRoute(orderId);
      await fetchOrderDetailsFn();
      showSuccess('Order removed from route successfully');
    } catch (e) {
      safeShowError(e);
    } finally {
      setRouteActionLoading(false);
    }
  };

  const markOrderPacked = async () => {
    setOrderPackedModal(false);
    setAddBagsToOrderModalOpen(true);
    setOrderActionLoading(true);
    try {
      await markOrderAsPacked(orderId);
      await fetchOrderDetailsFn();
      showSuccess('Order marked as PACKED successfully');
    } catch (e) {
      safeShowError(e);
    } finally {
      setOrderActionLoading(false);
    }
  };

  const markOrderDelivered = async () => {
    setOrderActionLoading(true);
    try {
      await markOrderAsDelivered(orderId);
      await fetchOrderDetailsFn();
      showSuccess('Order marked as DELIVERED successfully');
    } catch (e) {
      safeShowError(e);
    } finally {
      setOrderActionLoading(false);
    }
  };

  const markOrderUnpacked = async () => {
    setOrderUnPackedActionLoading(true);
    try {
      await markOrderAsUnpacked(orderId);
      await fetchOrderDetailsFn();
      showSuccess('Order marked as Unpacked successfully.');
    } catch (e) {
      safeShowError(e);
    } finally {
      setOrderUnPackedActionLoading(false);
    }
  };

  const setSalesPersonInOrderFn = async ({ userId }) => {
    try {
      await setSalesPersonInOrder({ orderId, userId });
      await fetchOrderDetailsFn();
      showSuccess('Sales person added successfully.');
    } catch (e) {
      safeShowError(e);
    }
  };
  
  const printQRCode = () => {
    const printContent = document.getElementById('pdf');
    const PrintWindow = window.open('', '', 'left=100,top=50,width=900,height=600,toolbar=1,scrollbars=1,status=0');
    if (printContent && PrintWindow) {
      const qrCodeElement = printContent.childNodes[0].childNodes[0].childNodes[0];
      const clonedQRCodeElement = qrCodeElement.cloneNode(true);
      PrintWindow.document.body.appendChild(clonedQRCodeElement);
      PrintWindow.document.body.style.display = 'flex';
      PrintWindow.document.body.style.justifyContent = 'center';
      PrintWindow.document.body.style.alignItems = 'center';
      PrintWindow.focus();
      PrintWindow.print();
    }
  };

  const getRouteAction = () => {
    const handleAddRoute = () => {
      setOrderIdForModal(orderId);
      setAddToRouteModalOpen(true);
    };

    return (
      <ActionButton
        text={order.route ? 'Remove' : 'Add'}
        active={!routeActionLoading}
        isLoading={routeActionLoading}
        onClick={order.route ? removeFromRoute : handleAddRoute}
        customClass={order.route ? styles['delete'] : ''}
      />
    );
  };

  const handleUpdateOrderItemQuantity = (orderItemId, count) => {
    setOrderItemToUpdate({ orderItemId, count });
    setUpdateOrderItemModalOpen(true);
  };

  const getAddOrderItemButton = () => {
    if (order.source === 'HOMEBOX' || order.isHomeBoxOrder) {
      return (
        // TODO: make this green
        <ActionButton
          text={'Add Item'}
          active={true}
          isLoading={false}
          onClick={() => setAddOrderItemModalOpen(true)}
          customClass={'ml-4'}
        />
      );
    }
    return null;
  };

  const getItemsSection = () => {
    const setSelectedItems = (newSelectedRecords: Record<string, any>[]) => {
      setSelectedRowsForRefund(newSelectedRecords);
      if (selectedRowsForRefund.length && !newSelectedRecords.length) {
        setRefundButtonDisabled(true);
      } else {
        setRefundButtonDisabled(false);
      }
    };
    const itemsSelectable = order?.salesChannel?.apiProvider !== SALES_CHANNEL_TYPES.UBER;
    return (
      <div className={styles['items-section']}>
        <div className={styles['subheading']}>
          <div>Items</div>
          {/* {!order.isExternalOrder ? ( */}
          <div className={styles['refund-button']}>
            <ActionButton
              text={'Refund'}
              active={!refundButtonDisabled}
              isLoading={false}
              onClick={() => setRefundModalOpen(true)}
            />
          </div>
          {getAddOrderItemButton()}
          {/* ): null} */}
        </div>
        <OrderItems
          {...props}
          items={orderItems}
          // itemsSelectable={!order.isExternalOrder}
          itemsSelectable={itemsSelectable}
          selectedRowsForRefund={selectedRowsForRefund}
          setSelectedForRefundRows={setSelectedItems}
          handleUpdateOrderItemQuantity={handleUpdateOrderItemQuantity}
          setOrderPackedModal={setOrderPackedModal}
          refresh={fetchOrderDetailsFn}
          order={order}
        />
      </div>
    );
  };

  const getCommentsSection = () => (
    <div className={styles['comments-section']}>
      <div className={styles['subheading']}>Comments</div>
      <div className={styles['comment-container']}>
        <div className={styles['details-group']}>
          <div className={styles['label']}>Operator Comments</div>
          <textarea
            className={'form-control'}
            disabled={updateLoading}
            value={comment}
            onChange={e => setComment(e.target.value)}
          />
        </div>
        <ActionButton
          text={'Update'}
          active={!updateLoading}
          isLoading={updateLoading}
          onClick={updateOperatorCommentFn}
        />
      </div>
    </div>
  );

  const getRouteDetails = () => (isUserAuthorized(props.userRoles, [USER_ROLES.MANAGER, USER_ROLES.ADMIN]) &&
    <div className={styles['route-details-section']}>
      <div className={styles['route-details-header']}>
        <div className={styles['subheading']}>Route Details</div>
        {getRouteAction()}
      </div>
      {order.route ? (
        <div className={styles['route-info']}>
          <div className={styles['info']}>
            <div className={styles['details-group']}>
              <div className={styles['label']}>Route ID</div>
              <a
                href={`/routes/${order.route.objectId}`}
                className={styles['detail']}
              >
                {order.route.objectId}
              </a>
            </div>
            <div className={styles['details-group']}>
              <div className={styles['label']}>Name</div>
              <div className={styles['detail']}>{order.route.name || '-'}</div>
            </div>
            <div className={styles['details-group']}>
              <div className={styles['label']}>Created On</div>
              <div className={styles['detail']}>{formatDate(order.route.createdAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A)}</div>
            </div>
            <div className={`${styles['details-group']}`}>
              <div className={styles['label']}>Comment</div>
              <div className={styles['detail']}>{order.route.comment || '-'}</div>
            </div>
          </div>
          <div className={styles['info']}>
            <div className={styles['details-group']}>
              <div className={styles['label']}>Status</div>
              <div className={`${styles['status']} ${styles[order.route.status]}`}>
                {order.route.status}
              </div>
            </div>
            <div className={styles['details-group']}>
              <div className={styles['label']}>Locked</div>
              <div className={styles['detail']}>{order.route.locked?.toString() || '-'}</div>
            </div>
            <div className={styles['details-group']}>
              <div className={styles['label']}>Delivery Date</div>
              <div className={styles['detail']}>{formatDate(order.route.deliveryDate, DATE_FORMATS.MM_DD_YYYY)}</div>
            </div>
          </div>
        </div>
      ) : (
        <div className={styles['empty-route-message']}>No Route found</div>
      )}
    </div>
  );

  const getPaymentDetails = () => order.paymentMethod && isUserAuthorized(props.userRoles, [USER_ROLES.MANAGER, USER_ROLES.ADMIN]) ? (
    <div className={styles['payment-details-section']}>
      <div className={styles['subheading']}>Payment Method</div>
      <div className={styles['details-section']}>
        <div className={styles['details-group']}>
          <SavedPaymentMethods
            user={{}}
            savedMethods={[order.paymentMethod]}
          />
        </div>
      </div>
    </div>
  ) : null;

  const getCustomerName = () => {
    return order?.salesChannel ? order?.addressObject?.contactName : order.purchaser?.full_name;
  };

  const getCustomerDetails = () => (
    <div className={styles['customer-details-section']}>
      <div>
        <div className={styles['subheading']}>Customer Details</div>
      </div>
      <div className={styles['customer-info']}>
        <div className={styles['info-section-1']}>
          <div className={styles['details-group']}>
            <div className={styles['label']}>Customer ID</div>
            <a
              href={`/users/${order.purchaser?.objectId}`}
              className={styles['detail']}
            >
              {order.purchaser?.objectId}
            </a>
          </div>
          <div className={styles['details-group']}>
            <div className={styles['label']}>Name</div>
            <div className={styles['detail']}>{getCustomerName() || '-'}</div>
          </div>
          <div className={styles['details-group']}>
            <div className={styles['label']}>Email</div>
            <div className={styles['detail']}>{order.purchaser?.email || '-'}</div>
          </div>
          <div className={styles['details-group']}>
            <div className={styles['label']}>Phone</div>
            <div className={styles['detail']}>{order.purchaser?.phone || '-'}</div>
          </div>
          <div className={`${styles['details-group']} ${styles['address']}`}>
            <div className={styles['label']}>Address</div>
            <div className={styles['detail']}>{order.purchaser?.address || '-'}</div>
          </div>
        </div>
        <div className={styles['info-section-2']}>
          {order.purchaser?.account ? (
            <div>
              <div className={styles['details-group']}>
                <div className={styles['label']}>Account ID</div>
                <div className={styles['detail']}>
                  <a
                    href={`/accounts/${order.purchaser?.account?.objectId}`}
                    className={styles['detail']}
                  >
                    {order.purchaser?.account?.objectId || '-'}
                  </a>
                </div>
              </div>
              <div className={styles['details-group']}>
                <div className={styles['label']}>Account Name</div>
                <div className={styles['detail']}>{order.purchaser?.account.name || '-'}</div>
              </div>
            </div>
          ) : null}
          <div className={styles['qrcode-container']}>
            <div className={styles['label']}>Delivery Location</div>
            <QRCode value={order.googleMapURL} size={150} />
          </div>
        </div>
      </div>
    </div>
  );

  const getOrderStatus = () => {
    if (order.status === ORDER_STATUS.PENDING_PAYMENT) return 'PENDING_PAYMENT';
    return order.status;
  };

  const getDeliveryEstimationActions = () => {
    if (!order.deliveryEmailSent && isUserAuthorized(props.userRoles, [USER_ROLES.MANAGER, USER_ROLES.ADMIN])) {
      return <ActionButton
        active={true}
        isLoading={estimationLoading}
        onClick={sendEstimation}
        text={'Send Delivery Estimation Email'}
      />;
    }
    return null;
  };

  const getOrderActions = () => {
    if (order.status === ORDER_STATUS.PAID || order.status === ORDER_STATUS.PENDING_PAYMENT || order.status === ORDER_STATUS.UNPACKED) {
      return <ActionButton
        active={!orderActionLoading}
        isLoading={orderActionLoading}
        onClick={markOrderPacked}
        text={'Mark Packed'}
      />;
    }
    if (order.status === ORDER_STATUS.PACKED || order.status === ORDER_STATUS.PENDING_PAYMENT) {
      return <ActionButton
        active={!orderActionLoading}
        isLoading={orderActionLoading}
        onClick={markOrderDelivered}
        text={'Mark Delivered'}
      />;
    }
    return null;
  };

  const getUnpackOrderButton = () => {
    if (order.status === ORDER_STATUS.PACKED) {
      return <ActionButton
        active={!orderUnPackedActionLoading}
        isLoading={orderUnPackedActionLoading}
        onClick={markOrderUnpacked}
        text={'Mark UnPacked'}
      />;
    }
    return null;
  };

  const getRequestCourierAction = () => {
    const courierUnavailableStatuses = [ORDER_STATUS.UNPAID, ORDER_STATUS.DELIVERED, ORDER_STATUS.CANCELLED];
    if (!_.includes(courierUnavailableStatuses, order.status)) {
      return <div>
        <ActionButton
          text={'Request Courier'}
          active={!order.uvaOrderId}
          isLoading={false}
          onClick={() => setCourierDeliveryTypeModalOpen(true)}
        />
      </div>;
    }
    return null;
  };

  const getPaymentActions = () => {
    if (order.waitingForPayment && isUserAuthorized(props.userRoles, [USER_ROLES.MANAGER, USER_ROLES.ADMIN])) {
      return (
        <>
          <ActionButton
            text={'Mark Payment Received'}
            active={true}
            isLoading={false}
            onClick={() => setMarkPaymentModalOpen(true)}
          />
          <ActionButton
            text={'Pay Now'}
            active={true}
            isLoading={false}
            onClick={() => setPaymentMethodModalOpen(true)}
          />
        </>
      );
    }
    return null;
  };

  const getCourierRequestView = () => {
    const trackingUrl = _.get(order, 'uvaResponse.data.trackingUrl', '');
    const orderId = _.get(order, 'uvaOrderId');
    if (trackingUrl) {
      return (
        < a
          href={trackingUrl}
          target='_blank'
          rel="noopener noreferrer"
          className={styles['detail']}
        >
          {orderId}
        </a>
      );
    }
    return <p>{orderId || `An error occurred while requesting for courier: ${_.get(order, 'uvaResponse.error')}`}</p>;
  };

  const getOrderTotalDetailsSection = () => (
    <div className={styles['order-total-section']}>
      <div className={styles['subheading']}>
        <div>Order Total Breakdown</div>
      </div>
      <div className={styles['details-section']}>
        <div className={styles['order-total-group']}>
          <div className={styles['charge-type']}>Subtotals</div>
          <div className={styles['charge-value']}>{order.subtotals ? <Currency amount={order.subtotals} /> : '-'}</div>
        </div>
        <div className={styles['order-total-group']}>
          <div className={styles['charge-type']}>Taxes</div>
          <div className={styles['charge-value']}>{order.taxes ? <Currency amount={order.taxes} /> : '-'}</div>
        </div>
        <div className={styles['order-total-group']}>
          <div className={styles['charge-type']}>Service Fees</div>
          <div className={styles['charge-value']}>{order?.serviceFee ? <Currency amount={order.serviceFee} /> : '-'}</div>
        </div>
        <div className={styles['order-total-group']}>
          <div className={styles['charge-type']}>Service Fees Taxes</div>
          <div className={styles['charge-value']}>{order?.serviceFeeTaxes ? <Currency amount={order.serviceFeeTaxes} /> : '-'}</div>
        </div>
        <div className={styles['order-total-group']}>
          <div className={styles['charge-type']}>Delivery Charges</div>
          <div className={styles['charge-value']}>{order.deliveryCharges ? <Currency amount={order.deliveryCharges} /> : '-'}</div>
        </div>
        <div className={styles['order-total-group']}>
          <div className={styles['charge-type']}>Delivery Taxes</div>
          <div className={styles['charge-value']}>{order.deliveryTaxes ? <Currency amount={order.deliveryTaxes} /> : '-'}</div>
        </div>
        <div className={styles['order-total-group']}>
          <div className={styles['charge-type']}>Discounted Amount</div>
          <div className={styles['charge-value']}>
            <Currency
              amount={order?.discountAmount ? order.discountAmount : 0}
              negative={true}
            />
          </div>
        </div>
        <div className={styles['order-total-group']}>
          <div className={`${styles['charge-type']} font-weight-bold`}>Total</div>
          <div className={styles['charge-value']}>{order.total ? <Currency amount={order.total} /> : '-'}</div>
        </div>
        <div className={styles['order-total-group']}>
          <div className={styles['charge-type']}>Refunded Amount</div>
          <div className={styles['charge-value']}>
            <Currency
              amount={order.refundedAmount ? order.refundedAmount : 0}
              negative={true}
            />
          </div>
        </div>
        <div className={styles['order-net-total-group']}>
          <div className={styles['charge-type']}>Net Payment</div>
          <div className={styles['charge-value']}>{order.netPayment ? <Currency amount={CentsToDollar(order.netPayment)} /> : '-'}</div>
        </div>
      </div>
    </div>
  );

  const getPackedDetails = () => {
    if (order?.status === ORDER_STATUS.PACKED || order?.status === ORDER_STATUS.DELIVERED) {
      return (
        <>
          <div className={styles['details-group']}>
            <div className={styles['label']}>Packed By</div>
            <div className={styles['detail']}>
              <CustomLink
                text={order?.packedBy?.id}
                linkTo={`/users/${order?.packedBy?.id}`}
              />
            </div>
          </div>
          <div className={styles['details-group']}>
            <div className={styles['label']}>Packed At</div>
            <div className={styles['detail']}>{order?.packedAt ? formatDate(order.packedAt, DATE_FORMATS.MM_DD_YYYY___hh_mm_A) : '-'}</div>
          </div>
          <div className={styles['details-group']}>
            <div className={styles['label']}>Total Packing Time</div>
            <div className={styles['detail']}>{timeToString(getDifferenceBetweenDates(order?.packedAt, order?.packingStartedAt))}</div>
          </div>
        </>
      );
    }
    else {
      return null;
    }
  };

  const getPackagingStartTime = () => {
    if (order?.packingStartedAt)
      return (
        <div className={styles['details-group']}>
          <div className={styles['label']}>Packing Started At</div>
          <div className={styles['detail']}>
            {order.packingStartedAt ? formatDate(order?.packingStartedAt, DATE_FORMATS.MM_DD_YYYY___hh_mm_A) : '-'}
          </div>
        </div>
      );
    else {
      return null;
    }
  };
  const getOrderBagTypes = () => {
    if (!_.isEmpty(order?.bags)) {
      return (
        <div className='d-flex justify-content-between'>
          <div className={styles['details-group']}>
            <div className={styles['label']}>Paper Bag</div>
            <div className={styles['detail']}>{order?.bags[ORDER_BAG_TYPES.PAPER_BAG] ?? '-'}</div>
          </div>
          <div className={styles['details-group']}>
            <div className={styles['label']}>Plastic Bag</div>
            <div className={styles['detail']}>{order?.bags[ORDER_BAG_TYPES.PLASTIC_BAG] ?? '-'}</div>
          </div>
          <div className={styles['details-group']}>
            <div className={styles['label']}>Delicate Items</div>
            <div className={styles['detail']}>{order?.bags[ORDER_BAG_TYPES.DELICATE_ITEM] ?? '-'}</div>
          </div>
        </div>
      );
    } else {
      return null;
    }
  };

  const [selectSalesPersonModalOpen, setSelectSalesPersonModalOpen] = useState(false);
  const toggleSelectSalesPersonModal = () => {setSelectSalesPersonModalOpen(!selectSalesPersonModalOpen)};

  const getAccountDetails = () => {
    if (!order?.account) return null;
    return (
      <div>
        <div className={styles['details-group']}>
          <div className={styles['label']}>Account</div>
          <CustomLink
            text={order.account.name}
            linkTo={`/accounts/${order.account.objectId}`}
          />
        </div>
        <div className={styles['details-group']}>
          <div className={styles['label']}>
            Sales Person 
            <span className={styles['edit-sales-person']} onClick={toggleSelectSalesPersonModal}>
              <i className={'fa fa-pencil'} />
            </span>
          </div>
          {order?.salesPerson?.full_name ?<CustomLink
            text={order?.salesPerson?.full_name}
            linkTo={`/users/${order.salesPerson?.objectId}`}
          /> : '-'}
        </div>
        {selectSalesPersonModalOpen && 
        <SalesPersonModal 
          toggleModal={toggleSelectSalesPersonModal}
          title="Select sales person"
          onSubmit={setSalesPersonInOrderFn}
          selectedUser={order.salesPerson}
        />
        }
      </div>
    );
  };

  const getSalesChannelDetails = () => {
    if (!order?.salesChannel) return null;
    return(
      <div>
        <div className={styles['details-group']}>
          <div className={styles['label']}>Sales Channel</div>
          <CustomLink
            text={order.salesChannel.name}
            linkTo={`/sales-channels/${order.salesChannel.objectId}`}
          />
        </div>
        <div className={styles['details-group']}>
          <div className={styles['label']}>Sales Channel Order Id</div>
          <div className={styles['detail']}>{order.salesChannelOrderId || '-'}</div>
        </div>
      </div>
    );
  };

  const getRefundAndCancelButton = () => {
    if (order.status === ORDER_STATUS.CANCELLED) return null;
    return (
      <ActionButton
        text={'Refund and Cancel'}
        active={true}
        isLoading={false}
        onClick={() => setOpenFullRefundModal(true)}
      />
    );
  };

  const getPaymentProofImage =() =>(
    order.paymentProofImage && <div className={styles['image-section']}>
      <div className={styles['image-container']}>
        <img
          src={order.paymentProofImage}
          onClick={() => setViewImageModalOpen(!viewImageModalOpen)}
        />
      </div>
    </div>
  );

  const getBasicDetails = () => (
    <div className={styles['basic-details-section']}>
      <div className={styles['subheading']}>
        <div>Basic Order Details</div>
      </div>
      <div className={styles['details-section']}>
        <div className={`mb-3 mb-md-0`}>
          <div className={styles['details-group']}>
            <div className={styles['label']}>Order ID</div>
            <div className={`${styles['detail']} ${styles['id']}`}>{order.objectId}</div>
          </div>
          <div className={styles['details-group']}>
            <div className={styles['label']}>Status</div>
            <div className={`${styles['status']} ${styles[getOrderStatus()]}`}>
              {order.status}
            </div>
          </div>
          {getPackagingStartTime()}
          {getPackedDetails()}
          {order?.deliveryEmailSent && <div className={styles['details-group']}>
            <div className={styles['label']}>Delivery Estimation Email sent: </div>
            <div className={styles['detail']}>{'Yes'}</div>
          </div>
          }
          <div className={styles['details-group']}>
            <div className={styles['label']}>External Order</div>
            <div className={styles['detail']}>{order.isExternalOrder?.toString() || 'false'}</div>
          </div>
          <div className={styles['details-group']}>
            <div className={styles['label']}>Payment Proof</div>
            <div className={styles['detail']}>{PAYMENT_PROOF_OPTIONS_MAPPING[order.paymentProof] || order.paymentProof || 'NA'}</div>
          </div>
          {getPaymentProofImage()}
          <div className={styles['details-group']}>
            <div className={styles['label']}>Waiting For Payment</div>
            <div className={styles['detail']}>{order.waitingForPayment ? 'Yes' : 'No'}</div>
          </div>
          <div className={styles['details-group']}>
            <div className={styles['label']}>Order Total</div>
            <div className={styles['detail']}><Currency amount={order.total} /></div>
          </div>
          <div className={styles['details-group']}>
            <div className={styles['label']}>Order Cost</div>
            <div className={styles['detail']}><Currency amount={order.cost} /></div>
          </div>
          {order.selectedDeliveryMethod && (
            <div className={styles['details-group']}>
              <div className={styles['label']}>Delivery Method</div>
              <div className={styles['detail']}>{capitalize(order.selectedDeliveryMethod)}</div>
            </div>
          )}
          {order.preferredDeliveryDay && (
            <div className={styles['details-group']}>
              <div className={styles['label']}>Preferred Delivery Day</div>
              <div className={styles['detail']}>{order.preferredDeliveryDay}</div>
            </div>
          )}
          {order?.discountCode && (
            <div className={styles['details-group']}>
              <div className={styles['label']}>Discount Code</div>
              <div className={styles['detail']}>{order.discountCode}</div>
            </div>
          )}
          {order.discountAmount !== 0 && (
            <div className={styles['details-group']}>
              <div className={styles['label']}>Discounted Amount</div>
              <div className={styles['detail']}><Currency amount={order.discountAmount} negative /></div>
            </div>
          )}
          {order.refundedAmount && (
            <div className={styles['details-group']}>
              <div className={styles['label']}>Refunded Amount</div>
              <div className={styles['detail']}><Currency amount={order.refundedAmount} negative /></div>
            </div>
          )}
          <div className={styles['details-group']}>
            <div className={styles['label']}>Order Made On</div>
            <div className={styles['detail']}>{formatDate(order.createdAt, DATE_FORMATS.MM_DD_YYYY_hh_mm_A)}</div>
          </div>
          {getOrderBagTypes()}
          {order.isHomeBoxOrder ? (
            <div className={styles['details-group']}>
              <div className={styles['label']}>User Subscription</div>
              <a
                href={`/subscriptions/${order.userSubscription?.objectId}`}
                className={styles['detail']}
              >
                {order.userSubscription?.objectId}
              </a>
            </div>
          ) : null}
          {order.courierRequested ? (
            <div className={styles['details-group']}>
              <div className={styles['label']}>Uva Order ID</div>
              {getCourierRequestView()}
            </div>
          ) : null}
          <div className={`${styles['details-group']} ${styles['address']}`}>
            <div className={styles['label']}>Delivery Address</div>
            <div className={styles['detail']}>{formatAddress(order) || '-'}</div>
          </div>
          <div className={styles['details-group']}>
            <div className={styles['label']}>Notes</div>
            <div className={styles['detail']}>{order.notes || '-'}</div>
          </div>
          <div className={styles['details-group']}>
            <div className={styles['label']}>Print PDF</div>
            <a href={`${process.env.REACT_APP_WEB_APP_URL}/orders/${orderId}`} target={'blank'}>
              {`${process.env.REACT_APP_WEB_APP_URL}/orders/${orderId}`}
            </a>
          </div>
          {getSalesChannelDetails()}
          {getAccountDetails()}
        </div>
        <div>
          <div className={styles['buttons-container']}>
            {getDeliveryEstimationActions()}
            {getOrderActions()}
            {getUnpackOrderButton()}
            {/* {!order.isExternalOrder ? ( */}
            {getRequestCourierAction()}
            {(order.status !== ORDER_STATUS.UNPAID && isUserAuthorized(props.userRoles, [USER_ROLES.MANAGER, USER_ROLES.ADMIN])) && (
              <ActionButton
                text={'Resend Email'}
                active={true}
                isLoading={false}
                onClick={() => setResendEmailModalOpen(true)}
              />
            )}
            {isUserAuthorized(props.userRoles, [USER_ROLES.MANAGER, USER_ROLES.ADMIN]) ?
              <div className={styles['refund-buttons']}>
                <ActionButton
                  text={'Refund Delivery'}
                  active={!deliveryRefundLoading}
                  isLoading={deliveryRefundLoading}
                  onClick={refundOrderDelivery}
                />
                {getRefundAndCancelButton()}
              </div> : null
            }
            {getPaymentActions()}
            {/* ): null} */}
          </div>
        </div>
      </div>
    </div>
  );

  const getOrderTimeline = () => {
    if (!_.isEmpty(order?.events)) {
      return <OrderTimeline events={order?.events} />;
    } else {
      return null;
    }
  };

  const getModals=()=>{
    if (viewImageModalOpen) return (
      <ViewImageModal
        originalAspect={true}
        source={order.paymentProofImage}
        toggleModal={() => setViewImageModalOpen(!viewImageModalOpen)}
      />
    );
    else return null;
  };
  const getHeader = () => (
    <div className={styles['header']}>
      <div className={styles['heading']}>Order Details</div>
      <div className={styles['header-buttons']}>
        <BackButton />
        <ActionButton
          text={'Print QR Code'}
          active={true}
          isLoading={false}
          onClick={() => printQRCode()}
        />
      </div>
    </div>
  );


  if (loading) return <Loading />;

  return (
    <>
      {addToRouteModalOpen && (
        <AddOrderToRouteModal
          orderIds={orderIdForModal}
          onSuccess={() => {
            fetchOrderDetailsFn();
          }}
          toggleModal={() => {
            setOrderIdForModal('');
            setAddToRouteModalOpen(!addToRouteModalOpen);
          }}
        />
      )}
      {(markPaymentModalOpen || paymentMethodModalOpen) &&
        <MakePaymentModal
          userId={order.purchaser?.objectId}
          orderId={orderId}
          isPaid={markPaymentModalOpen}
          onSuccess={fetchOrderDetailsFn}
          toggleModal={() => {
            if (markPaymentModalOpen) {
              setMarkPaymentModalOpen(false);
            } else {
              setPaymentMethodModalOpen(false);
            }
          }}
        />
      }
      {refundModalOpen && (
        <RefundItemsModal
          orderId={orderId}
          onSuccess={fetchOrderDetailsFn}
          items={selectedRowsForRefund}
          setItems={setSelectedRowsForRefund}
          toggleModal={() => setRefundModalOpen(false)}
        />
      )}
      {openFullRefundModal && (
        <FullRefundModal
          orderId={orderId}
          onSuccess={fetchOrderDetailsFn}
          toggleModal={() => setOpenFullRefundModal(false)}
        />
      )}
      {addOrderItemModalOpen && (
        <AddOrderItemModal
          orderId={orderId}
          onSuccess={fetchOrderDetailsFn}
          toggleModal={() => setAddOrderItemModalOpen(false)}
        />
      )}
      {updateOrderItemModalOpen && (
        <UpdateOrderItemModal
          onSuccess={fetchOrderDetailsFn}
          toggleModal={() => setUpdateOrderItemModalOpen(false)}
          orderItemToUpdate={orderItemToUpdate}
        />
      )}
      {courierDeliveryTypeModalOpen && (
        <CourierDeliveryTypeModal
          orderId={orderId}
          onSuccess={fetchOrderDetailsFn}
          toggleModal={() => setCourierDeliveryTypeModalOpen(!courierDeliveryTypeModalOpen)}
        />
      )}
      {resendEmailModalOpen && (
        <ResendEmailModal
          userEmail={order.purchaser.email}
          orderId={orderId}
          onSuccess={fetchOrderDetailsFn}
          toggleModal={() => setResendEmailModalOpen(!resendEmailModalOpen)}
        />
      )}
      {addBagsToOrderModalOpen && (
        <AddBagTypesToOrder
          orderId={orderId}
          onSuccess={fetchOrderDetailsFn}
          toggleModal={() => setAddBagsToOrderModalOpen(!addBagsToOrderModalOpen)}
        />
      )}
      {orderPackedModal &&
        <InfoModal
          cancellable={true}
          type={MODAL_TYPES.CONFIRM_CHANGES}
          title={'Order'}
          message={'All Items are packed. Do you want to mark the order as packed?'}
          toggleModal={() => setOrderPackedModal(!orderPackedModal)}
          primaryButtonText={'Mark Order Packed'}
          primaryButtonOnClick={markOrderPacked}
          secondaryButtonText={'Cancel'}
          secondaryButtonOnClick={() => {
            setOrderPackedModal(false);
          }}
        />
      }
      <div id={'main-container'} className={`${styles['main-container']}`}>
        {getHeader()}
        {fetchError && (
          <div className="alert alert-danger">
            <div><strong>Failed to load order</strong></div>
            <div>{fetchError}</div>
          </div>
        )}
        {fetchError ? null : (
          <>
            {getBasicDetails()}
            {getOrderTotalDetailsSection()}
            {getOrderTimeline()}
            {getCustomerDetails()}
            {getPaymentDetails()}
            {getRouteDetails()}
            {getCommentsSection()}
            {getItemsSection()}
            {getModals()}
          </>
        )}
      </div>
      <div id={'pdf'} className={styles['pdf']}>
        <QRCodeGenerator
          qrCodeURLs={[
            `${baseURL}/orders/${orderId}`,
          ]}
        />
      </div>
    </>
  );
};

const OrderDetailsWrapper = connect(mapStateToProps, { ...authActions })(OrderDetails);
export { OrderDetailsWrapper as OrderDetails };